import { assertExhaustiveSwitch } from '@paragon/utilities';

import { getCurrentJwtToken } from './dataConfiguration';

export interface ParagonFetchResult {
  readonly response: unknown;
  readonly headers: Headers;
  readonly statusCode: number;
}

/**
 * If you want to check the status code returned from an ASP.NET server, you probably want `AspNetStatusCodeResult`
 * (assuming the `errorHandlingMiddleware` is being used, which it is for most requests).
 */
export interface ParagonFetchFailure {
  readonly response: unknown;
  readonly statusCode: number;
}

interface ParagonFetchOptions {
  readonly includeAuthorizationHeader?: boolean;
  readonly encodeRoute?: boolean;
  readonly cancellationPromise?: PromiseLike<void>;
  readonly responseType?: 'json-or-text' | 'blob' | 'stream';

  readonly data?: unknown;
  readonly params?: URLSearchParams;
}

export async function paragonFetch(
  method: string,
  route: string,
  options: ParagonFetchOptions = {},
): Promise<ParagonFetchResult> {
  const { includeAuthorizationHeader = true, encodeRoute = true, responseType = 'json-or-text' } = options;

  if (encodeRoute) {
    route = encodeURI(route);
  }

  if (options?.params) {
    route += `?${options.params}`;
  }

  const requestHeaders = new Headers();

  const jwt = getCurrentJwtToken();
  if (includeAuthorizationHeader && jwt) {
    requestHeaders.append('Authorization', `JWT ${jwt}`);
  }

  let requestBody: string | undefined = undefined;
  if (options.data !== undefined) {
    requestHeaders.append('Content-Type', 'application/json');
    requestBody = JSON.stringify(options.data);
  }

  let abortSignal: AbortSignal | undefined = undefined;
  if (options.cancellationPromise) {
    const abortController = new AbortController();
    abortSignal = abortController.signal;
    options.cancellationPromise.then(() => {
      abortController.abort();
    });
  }

  const response = await fetch(route, {
    method,
    headers: requestHeaders,
    body: requestBody,
    signal: abortSignal,
  });

  if (!response.ok) {
    const responseValue = await parseJsonOrTextResponse(response);
    const failure: ParagonFetchFailure = {
      response: responseValue,
      statusCode: response.status,
    };
    throw failure;
  }

  let responseValue: unknown;
  switch (responseType) {
    case 'json-or-text':
      // A 204 has no body, which would otherwise come back as an empty string.
      responseValue = response.status === 204 ? null : await parseJsonOrTextResponse(response);
      break;
    case 'blob':
      responseValue = await response.blob();
      break;
    case 'stream':
      responseValue = response.body;
      break;
    default:
      assertExhaustiveSwitch(responseType);
  }

  const result: ParagonFetchResult = {
    response: responseValue,
    headers: response.headers,
    statusCode: response.status,
  };
  return result;
}

async function parseJsonOrTextResponse(response: Response): Promise<unknown> {
  // Some routes have an empty response which is not valid JSON. We can check the content-type to see whether the
  // response should be valid JSON.
  const contentType = response.headers.get('content-type');
  // Normally the content type will be `application/json`, but it can also be `application/problem+json` when the
  // ASP.NET servers responds with a NotFound status result, for example.
  if (contentType && contentType.includes('json')) {
    return await response.json();
  }

  return await response.text();
}
